import React, { useState, useEffect } from 'react';
import './GoogleReviewFloating.css';
import { FaGoogle, FaStar, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';
import { GOOGLE_REVIEW_URL } from '../../constants/reviewConstants';

const STORAGE_KEY = 'bappa_google_review_dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * GoogleReviewFloating Component
 * 
 * Non-intrusive floating review widget that appears after a short delay
 * and allows website visitors to easily access Google Maps reviews.
 */
const GoogleReviewFloating = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Check if dismissed recently
    try {
      const dismissedAt = localStorage.getItem(STORAGE_KEY);
      if (dismissedAt) {
        const timePassed = Date.now() - parseInt(dismissedAt, 10);
        if (timePassed < DISMISS_DURATION_MS) {
          return; // Do not show if dismissed recently
        }
      }
    } catch (e) {
      console.warn('LocalStorage error:', e);
    }

    // Delay appearance so it is completely non-intrusive (appears after 4 seconds)
    const timer = setTimeout(() => {
      setIsVisible(true);
      // Gently expand after entering
      const expandTimer = setTimeout(() => {
        setIsExpanded(true);
      }, 500);
      return () => clearTimeout(expandTimer);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = (e) => {
    e.stopPropagation();
    setIsExpanded(false);
    setIsVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch (err) {
      console.warn('Could not save dismissal state:', err);
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <aside className="google-review-floating-root" aria-label="Google Review Reminder">
      {isExpanded ? (
        <div className="google-review-floating-card" role="dialog" aria-labelledby="floating-review-title">
          <button
            className="floating-close-btn"
            onClick={handleDismiss}
            aria-label="Close review reminder"
            title="Close"
          >
            <FaTimes />
          </button>

          <div className="floating-card-header">
            <div className="floating-google-pill">
              <FaGoogle className="floating-g-icon" />
              <span>Google Review</span>
            </div>
            <div className="floating-stars-row" aria-label="5 stars">
              {[...Array(5)].map((_, idx) => (
                <FaStar key={idx} className="floating-star" />
              ))}
            </div>
          </div>

          <h4 id="floating-review-title" className="floating-card-title">
            Enjoying Bappa Taxi Services?
          </h4>
          <p className="floating-card-text">
            Share your travel experience on Google Maps and help other travelers!
          </p>

          <div className="floating-card-actions">
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="floating-review-cta-btn"
              onClick={() => {
                try {
                  localStorage.setItem(STORAGE_KEY, Date.now().toString());
                } catch (e) { }
              }}
              aria-label="Write a Google Review for Bappa Taxi Services (opens in a new tab)"
            >
              <span>⭐ Write a Google Review</span>
              <FaExternalLinkAlt className="floating-btn-icon" />
            </a>
          </div>
        </div>
      ) : (
        <button
          className="google-review-floating-pill-btn"
          onClick={handleToggleExpand}
          aria-label="Open Google review prompt"
        >
          <span className="floating-pill-g"><FaGoogle /></span>
          <span className="floating-pill-text">Review Us ⭐</span>
        </button>
      )}
    </aside>
  );
};

export default GoogleReviewFloating;
